import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import FilterPanel from "../../components/FilterPanel";
import Input from "../../components/Input";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import Select from "../../components/Select";
import Table from "../../components/Table";
import UserCell from "../../components/UserCell";
import { api } from "../../services/api";

const SubjectCell = ({ subject }) =>
  subject ? (
    <div>
      <div className="font-medium">{subject.name}</div>
      <div className="text-xs text-gray-500">{subject.code}</div>
    </div>
  ) : (
    <span className="italic text-gray-400">N/A</span>
  );

const PreferredTimesCell = ({ times }) => {
  if (!times || times.length === 0)
    return <span className="italic text-gray-400">Not provided</span>;

  const formatted = times.map((slot) => {
    const dateStr = slot.date
      ? dayjs(slot.date).format("MMM DD, YYYY")
      : "Unscheduled";
    return `${dateStr} ${slot.start || ""}${
      slot.end ? ` - ${slot.end}` : ""
    }`.trim();
  });

  if (formatted.length === 1) return <span>{formatted[0]}</span>;

  return (
    <div>
      <div>{formatted[0]}</div>
      <div className="text-xs text-gray-500">+ {formatted.length - 1} more</div>
    </div>
  );
};

export default function AdminBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [cancellingId, setCancellingId] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: 50,
        page,
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await api.get(`/admin/bookings?${params}`, {
        withCredentials: true,
      });

      if (response?.success) {
        let data = response.bookings || [];

        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          data = data.filter((b) => {
            const studentName = `${b.studentId?.name?.first || ""} ${
              b.studentId?.name?.last || ""
            }`.toLowerCase();
            const studentEmail = b.studentId?.email?.toLowerCase() || "";
            const tutorName = `${b.assignedTutorId?.name?.first || ""} ${
              b.assignedTutorId?.name?.last || ""
            }`.toLowerCase();
            const tutorEmail = b.assignedTutorId?.email?.toLowerCase() || "";
            const subjectName = b.subjectId?.name?.toLowerCase() || "";
            const subjectCode = b.subjectId?.code?.toLowerCase() || "";
            return [
              studentName,
              studentEmail,
              tutorName,
              tutorEmail,
              subjectName,
              subjectCode,
            ].some((field) => field.includes(q));
          });
        }

        setBookings(data);
        setPagination(response.pagination || {});
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to fetch bookings"
      );
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleCancel = async (bookingId) => {
    const target = bookings.find((b) => b._id === bookingId);
    if (!target || target.status === "cancelled") return;
    const result = await Swal.fire({
      title: "Cancel Booking",
      text: "Are you sure you want to cancel this booking? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Booking",
      confirmButtonColor: "#d33",
      cancelButtonText: "Keep Booking",
    });
    if (!result.isConfirmed) return;
    try {
      setCancellingId(bookingId);
      await api.put(
        `/admin/bookings/${bookingId}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to cancel booking");
    } finally {
      setCancellingId(null);
    }
  };

  const statusVariantMap = {
    pending: "warning",
    awaiting_tutor_confirmations: "info",
    confirmed: "success",
    failed: "danger",
    cancelled: "danger",
  };

  const columns = [
    {
      header: "Student",
      sortable: false,
      render: (row) => <UserCell user={row.studentId} />,
    },
    {
      header: "Subject",
      sortable: false,
      render: (row) => <SubjectCell subject={row.subjectId} />,
    },
    {
      header: "Assigned Tutor",
      sortable: false,
      render: (row) => <UserCell user={row.assignedTutorId} />,
    },
    {
      header: "Preferred Times",
      sortable: false,
      render: (row) => <PreferredTimesCell times={row.preferredTimes} />,
    },
    {
      header: "Created",
      accessor: "createdAt",
      sortable: true,
      render: (row) =>
        row.createdAt
          ? dayjs(row.createdAt).format("MMM DD, YYYY HH:mm")
          : "N/A",
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      render: (row) => (
        <Badge
          variant={statusVariantMap[row.status] || "default"}
          label={row.status?.replace(/_/g, " ")}
          size="small"
          className="font-medium"
        />
      ),
    },
    {
      header: "Action",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/bookings/${row._id}`)}
          >
            View
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={row.status === "cancelled" || cancellingId === row._id}
            loading={cancellingId === row._id}
            onClick={() => handleCancel(row._id)}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    {
      value: "awaiting_tutor_confirmations",
      label: "Awaiting Tutor Confirmations",
    },
    { value: "confirmed", label: "Confirmed" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const hasFilters = statusFilter || searchQuery;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <PageHeader
        title="All Bookings"
        subtitle="Monitor and manage booking requests"
      />

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-md flex justify-between items-center">
          <span>{error}</span>
          <Button variant="danger" size="sm" onClick={() => fetchBookings()}>
            Retry
          </Button>
        </div>
      )}

      <FilterPanel title="Filters">
        <Input
          label="Search by name, email, or subject"
          placeholder="Search bookings..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
        />
        <Select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          options={statusOptions}
        />
      </FilterPanel>

      {hasFilters && (
        <div className="mb-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStatusFilter("");
              setSearchQuery("");
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
          <span className="text-sm text-text opacity-60 py-2">
            {bookings.length} bookings found
          </span>
        </div>
      )}

      <Table
        columns={columns}
        data={bookings}
        loading={loading}
        emptyMessage={
          hasFilters ? "No bookings match your filters" : "No bookings found"
        }
      />

      {pagination.pages > 1 && (
        <div className="mt-6 flex flex-col gap-4">
          <p className="text-sm text-text">
            Showing {bookings.length} of {pagination.total} bookings
          </p>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
